import { Stack, Text } from '@main/complykit';
import { EditableInputField, OptionsField, QuestionnaireComponentKind } from '@main/dynamic-form';
import { formatDate } from '@main/shared/utils';
import { useFormContext, useWatch } from 'react-hook-form';

import { NotAnswered } from './not-answered';

/* The read view: an answer as text, for a reviewer reading a whole
 * submission (form-engine.md, "Modes"). A value the reader came for is never
 * a control they cannot use. */

export const ReadInputField: EditableInputField = ({ config }) => {
  const { control } = useFormContext();
  const stored: unknown = useWatch({ control, name: config.name, defaultValue: config.value });
  // The answer column is JSON: a number reads as Answer mode shows it.
  const value = typeof stored === 'number' ? String(stored) : stored;

  if (!(typeof value === 'string' && value.trim())) {
    return <NotAnswered />;
  }

  // A stored config may name its kind without an input type; a value in another format reads as stored.
  const isDate =
    (config.type === 'date' ||
      ('kind' in config && config.kind === QuestionnaireComponentKind.Date)) &&
    /^[1-9]\d{3}-\d{2}-\d{2}$/.test(value);
  return (
    <Text size="sm" color="default">
      {isDate ? formatDate(value) : value}
    </Text>
  );
};

export const ReadOptionsField: OptionsField = ({ config }) => {
  const { control } = useFormContext();
  const value: unknown = useWatch({ control, name: config.name, defaultValue: config.value });
  const selected = (Array.isArray(value) ? value : value ? [value] : []).filter(
    (item): item is string => typeof item === 'string',
  );

  if (!selected.length) {
    return <NotAnswered />;
  }

  const labels = selected.map(
    (item) => config.options?.find((option) => option.value === item)?.label ?? item,
  );

  return (
    <Stack as="ul" gap={1}>
      {labels.map((label, index) => (
        <Stack as="li" key={selected[index]}>
          <Text size="sm" color="default">
            {label}
          </Text>
        </Stack>
      ))}
    </Stack>
  );
};
